// modules/dochadzka.js — dátové jadro modulu Dochádzka / Evidencia času.
// DRUHÝ modul cez generic runtime — DÔKAZ kompozície: iný „recept" (MODULE_DEF),
// ten istý render (ModuleScreen), 0 nového UI kódu, 0 kernel rozšírenia.
// Modul NIKDY nepridáva tabuľku — pridáva `module` hodnotu ('time_entry')
// + custom_fields nad univerzálnym records enginom. Rezervované stĺpce kernelu
// sa mapujú top-level PRESNE ako v sklad.js: qty = odpracované hodiny (desatinné
// OK, 7.5), unit = 'h', unit_cost_cents = hodinová sadzba v centoch,
// container_id = zákazka/job (most k peniazom: hodiny na zákazku = náklad
// zákazky, ako Sklad výdaj s container_id). VŠETKO ostatné ide do `custom`
// JSON stĺpca (records má explicitné stĺpce — nové top-level pole by INSERT
// zhodil, ako records.paid).
//
// Event-sourcing: time_entry je IMMUTABLE udalosť; odpracované hodiny/náklad
// = reduce záznamov (sumHours/laborCostCents), NIKDY uložený stav → žiadny
// sync konflikt stavu, LWW rieši len jednotlivé záznamy.
//
// 0 fetch / 0 LLM / 0 nových závislostí — čistá lokálna vrstva (forever-free).
import { db, newCommonFields } from '../db';
import { isValidIsoDate, todayIso } from '../dateUtils';

// ─── MODULE_DEF — deklaratívny descriptor modulu (DÁTA, žiadna logika) ────────
// Rovnaký Š2 tvar ako sklad.js. Builder z neho renderuje formulár, signals
// kŕmia mozog (buildDochadzkaSignals nižšie číta from_metric → METRIC_COMPUTE).
// access.max_tier_out: 2 = pracovník/kolaborant vidí hodiny, NIE sadzby
// (Tier-1) — tu len DEKLARÁCIA; redaction mechanika príde v surface balíku.
export const MODULE_DEF = {
  module: 'cas', // zosúladené s MODULE_MANIFEST id 'cas'
  label_sk: 'Evidencia času',
  // accent = jediný zdroj brand farby modulu (menu+header+KPI).
  accent: 'red',
  entity: {
    base: 'record',
    custom_fields: [
      { stable_id: 'f_note', label: 'Poznámka', type: 'text' },
      { stable_id: 'f_employee', label: 'Pracovník', type: 'text' },
    ],
  },
  google: { sheet_tab: 'Dochádzka', form: 'pipni-sa', calendar: true },
  triggers: [{ on: 'create', do: 'recomputeHours' }],
  view: { filter: { module: 'time_entry' }, sort: 'entry_date_desc' },
  access: { collaborator: true, max_tier_out: 2 },
  metrics: [
    { slug: 'odpracovane_hodiny', reduce: 'engine', hero: true },
    { slug: 'naklad_prace', reduce: 'engine', hero: true },
  ],
  signals: [
    { fact: 'dochadzka.labor_cost_cents', from_metric: 'naklad_prace', semantic: 'cash_out' },
    { fact: 'dochadzka.hours_total', from_metric: 'odpracovane_hodiny', semantic: 'informational' },
  ],
  surfaces: [
    {
      // „Pípni sa" — Podanie pre pracovníka (stub, len DEKLARÁCIA; surface
      // broker/HTML príde v surface balíku — honest states).
      surface_id: 'pipni-sa', kind: 'form_or_html', role: 'pracovník',
      input: ['entry_date', 'qty'],
      writes: { module: 'time_entry', reason: 'pipnutie' },
    },
  ],
  flows: ['flow:hodiny-do-faktury@1'],
};

// Celé nezáporné číslo v centoch (peniaze = int centy, nikdy float).
const isCents = (v) => Number.isInteger(v) && v >= 0;

// ─── createTimeEntry — IMMUTABLE záznam času (module='time_entry') ────────────
// input = { qty (hodiny > 0 povinné, desatinné OK), unit? (default 'h'),
//           unit_cost_cents? (hodinová sadzba, int centy), entry_date? (default
//           dnes), container_id? (RELATE na zákazku → náklad zákazky, OPTIONAL),
//           label?, note?, employee_ref? (counterparty) }
// qty/unit/unit_cost_cents/container_id → rezervované stĺpce kernelu;
// note/employee_ref → custom JSON. Záznam sa NIKDY neupravuje ani nemaže —
// hodiny/náklad sú vždy reduce záznamov (event-sourcing). Chyby hádže
// (write-side gate — jeden zlý záznam by rozbil sumHours/laborCostCents).
export async function createTimeEntry(input) {
  const qty = input?.qty;
  if (typeof qty !== 'number' || !Number.isFinite(qty) || qty <= 0) {
    throw new Error('qty musí byť číslo > 0');
  }
  // Chýbajúca sadzba je OK (záznam bez ceny) — ale keď príde, musí byť int centy.
  if (input.unit_cost_cents != null && !isCents(input.unit_cost_cents)) {
    throw new Error('unit_cost_cents musí byť celé nezáporné číslo v centoch');
  }
  if (input.entry_date != null && !isValidIsoDate(input.entry_date)) {
    throw new Error('entry_date musí byť valídny ISO dátum (YYYY-MM-DD)');
  }

  const record = {
    ...newCommonFields(),
    module: 'time_entry',
    entry_date: input.entry_date || todayIso(),
    label: typeof input.label === 'string' && input.label.trim() !== '' ? input.label.trim() : null,
    qty, // rezervované stĺpce kernelu ↓
    unit: input.unit || 'h',
    unit_cost_cents: input.unit_cost_cents ?? null,
    container_id: input.container_id || null, // RELATE na zákazku (idx_records_container_id)
    amount_cents: null,
    status: 'confirmed',
    source: input.source || 'manual',
    // note/employee_ref — JEDINÉ miesto je custom JSON (top-level by zhodilo INSERT)
    custom: {
      note: input.note || null,
      employee_ref: input.employee_ref || null,
    },
  };

  await db.records.add(record);
  return record;
}

// ─── Čisté reduce funkcie — hodiny/náklad sa NIKDY neukladajú ─────────────────

// Živý záznam času (nezmazaný time_entry). Nečíselné/nekladné qty sa ignoruje
// fail-safe (nefabrikuj hodiny) — createTimeEntry ho nepustí, toto chráni len
// pred cudzími/poškodenými dátami zo syncu.
const isLiveEntry = (r) =>
  r != null && r.module === 'time_entry' && r.deleted_at == null;

// Hodiny záznamu: konečné kladné číslo, inak 0 (žiadny NaN, žiadne záporné
// hodiny — time_entry nemá direction, záporné qty = poškodené dáta).
const entryHours = (r) => {
  const q = Number(r.qty);
  return Number.isFinite(q) && q > 0 ? q : 0;
};

// Odpracované hodiny = Σ qty živých time_entry. Optional jobRef filter na
// container_id (hodiny na jednu zákazku). Čistá funkcia nad poľom records.
export function sumHours(records, jobRef) {
  const recs = Array.isArray(records) ? records : [];
  let hours = 0;
  for (const r of recs) {
    if (!isLiveEntry(r)) continue;
    if (jobRef != null && r.container_id !== jobRef) continue;
    hours += entryHours(r);
  }
  return hours;
}

// Náklad práce v centoch = Σ qty × unit_cost_cents cez záznamy.
// Záznam bez sadzby prispieva 0 — advisor NIKDY nefabrikuje sumu (honest
// numbers): hodiny bez sadzby náklad nezvýšia, radšej priznaná nepresnosť
// než vymyslená cena. Zaokrúhlenie RAZ per záznam (qty smie byť desatinné,
// sadzba je int centy) — rovnaký per-záznam rounding pattern ako sklad
// stockValueCents (DECISIONS 2026-07-07).
export function laborCostCents(records) {
  const recs = Array.isArray(records) ? records : [];
  let cents = 0;
  for (const r of recs) {
    if (!isLiveEntry(r)) continue;
    if (!isCents(r.unit_cost_cents)) continue; // chýbajúca/zlá sadzba → 0 príspevok
    cents += Math.round(entryHours(r) * r.unit_cost_cents);
  }
  return cents;
}

// Hodiny per zákazka (1 prechod): { container_id → Σ hodín }. Záznam bez
// container_id (nie je na zákazku) sa do mapy nedostane — most k peniazom
// funguje len pre priradené hodiny.
export function hoursPerJob(records) {
  const recs = Array.isArray(records) ? records : [];
  const jobs = {};
  for (const r of recs) {
    if (!isLiveEntry(r)) continue;
    const ref = r.container_id;
    if (typeof ref !== 'string' || ref === '') continue;
    jobs[ref] = (jobs[ref] || 0) + entryHours(r);
  }
  return jobs;
}

// ─── buildDochadzkaSignals — fakty pre mozog (advisor snapshot) ───────────────
// Deklaratívne: fakty aj semantiky žijú v MODULE_DEF.signals; tu len mapa
// from_metric → výpočet. Vracia { 'dochadzka.labor_cost_cents': int (cash_out),
// 'dochadzka.hours_total': number (informational) }. Neznámy from_metric =
// chyba descriptora → throw (chyby vypíš, neprehltni).
const METRIC_COMPUTE = {
  odpracovane_hodiny: (records) => sumHours(records),
  naklad_prace: laborCostCents,
};

export function buildDochadzkaSignals(records) {
  const out = {};
  for (const sig of MODULE_DEF.signals) {
    const compute = METRIC_COMPUTE[sig.from_metric];
    if (!compute) {
      throw new Error(`dochadzka: signal '${sig.fact}' referencuje neznámu metriku '${sig.from_metric}'`);
    }
    out[sig.fact] = compute(records);
  }
  return out;
}
